import { formatDate } from "@plasma/utils/time";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { twMerge } from "tailwind-merge";

import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/** Calculate new date range based on the clicked date and current range */
function calculateNewDateRange(
  clickedDate: Date,
  currentRange: DateRange | undefined
): DateRange {
  // If we have a complete range (both from and to), start fresh
  if (currentRange?.to) {
    return { from: clickedDate, to: undefined };
  }

  // If we have a from date, determine the to date
  if (currentRange?.from) {
    const isClickedDateAfterFrom = clickedDate > currentRange.from;
    return {
      from: isClickedDateAfterFrom ? currentRange.from : clickedDate,
      to: isClickedDateAfterFrom ? clickedDate : currentRange.from,
    };
  }

  // No range yet, start with the clicked date as from
  return { from: clickedDate, to: undefined };
}

const defaultQuickOptions: { name: string; getValue: () => DateRange }[] = [
  {
    name: "Today",
    getValue: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      return { from: today, to: endOfDay };
    },
  },
  {
    name: "Yesterday",
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const endOfDay = new Date(yesterday);
      endOfDay.setHours(23, 59, 59, 999);
      return { from: yesterday, to: endOfDay };
    },
  },
  {
    name: "This Week",
    getValue: () => {
      const from = new Date();
      from.setDate(from.getDate() - from.getDay());
      from.setHours(0, 0, 0, 0);
      const to = new Date();
      to.setHours(23, 59, 59, 999);
      return { from, to };
    },
  },
  {
    name: "Last Week",
    getValue: () => {
      const from = new Date();
      const to = new Date();
      from.setDate(from.getDate() - 7 - from.getDay());
      from.setHours(0, 0, 0, 0);
      to.setDate(to.getDate() - to.getDay() - 1);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    },
  },
  {
    name: "This Month",
    getValue: () => {
      const from = new Date();
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      const to = new Date();
      to.setHours(23, 59, 59, 999);
      return { from, to };
    },
  },
  {
    name: "Last Month",
    getValue: () => {
      const from = new Date();
      const to = new Date();
      from.setMonth(from.getMonth() - 1);
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      to.setDate(0);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    },
  },
];

const projectionQuickOptions: { name: string; getValue: () => DateRange }[] = [
  {
    name: "Next Week",
    getValue: () => {
      const from = new Date();
      from.setDate(from.getDate() + (7 - from.getDay()));
      const to = new Date(from);
      to.setDate(to.getDate() + 6);
      return { from, to };
    },
  },
  {
    name: "Next 2 Weeks",
    getValue: () => {
      const from = new Date();
      from.setDate(from.getDate() + (7 - from.getDay()));
      const to = new Date(from);
      to.setDate(to.getDate() + 13);
      return { from, to };
    },
  },
  {
    name: "Next Month",
    getValue: () => {
      const from = new Date();
      from.setDate(1);
      from.setMonth(from.getMonth() + 1);
      const to = new Date(from);
      to.setMonth(to.getMonth() + 1);
      to.setDate(0);
      return { from, to };
    },
  },
  {
    name: "Next Quarter",
    getValue: () => {
      const from = new Date();
      from.setDate(1);
      from.setMonth(Math.floor((from.getMonth() + 3) / 3) * 3);
      const to = new Date(from);
      to.setMonth(to.getMonth() + 3);
      to.setDate(0);
      return { from, to };
    },
  },
];

const ONE_DAY_IN_MS = 86_400_000;

export const DateRangeInput = ({
  className,
  value,
  onValueChange,
  allowPastDates = true,
  customQuickOptions,
  placeholder = "Select a date range",
}: {
  className?: string;
  value: DateRange;
  onValueChange: (value: DateRange) => void;
  allowPastDates?: boolean;
  customQuickOptions?: { name: string; getValue: () => DateRange }[];
  placeholder?: string;
}) => {
  const [lastPressedButton, setLastPressedButton] = useState<string>();
  const [dateRange, setDateRange] = useState<DateRange>(value);

  let dateDifference = 0;
  if (value?.from && value?.to) {
    dateDifference =
      Math.floor((value.to.getTime() - value.from.getTime()) / ONE_DAY_IN_MS) +
      1;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const quickOptionsToUse =
    customQuickOptions ??
    (allowPastDates ? defaultQuickOptions : projectionQuickOptions);

  return (
    <Popover onOpenChange={(v) => !v && setDateRange(value)}>
      <PopoverTrigger asChild>
        <Button
          className={twMerge(
            "body text-muted-foreground flex h-auto flex-row items-center justify-center gap-2 rounded-md px-5 py-3 text-left",
            className
          )}
          type="button"
          variant="outline"
        >
          <CalendarIcon className="size-4" />
          {value?.from || value?.to ? (
            <span className="text-foreground font-sans font-normal">
              {dateDifference} day{dateDifference !== 1 && "s"} (
              {value.from ? formatDate(value.from, "D MMM, YYYY") : "?"} -{" "}
              {value.to ? formatDate(value.to, "D MMM, YYYY") : "?"})
            </span>
          ) : (
            <span className="text-muted-foreground font-sans font-normal">
              {placeholder}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="border-border flex w-auto rounded-lg border-2 p-0"
      >
        <Calendar
          defaultMonth={allowPastDates ? undefined : today}
          disabled={allowPastDates ? undefined : (date) => date < today}
          mode="range"
          numberOfMonths={2}
          onDayClick={(e) => {
            if (!e || (!allowPastDates && e < today)) {
              return;
            }
            const newDateRange = calculateNewDateRange(e, dateRange);

            setDateRange(newDateRange);
            if (newDateRange.to) {
              onValueChange(newDateRange);
            }
          }}
          selected={dateRange}
        />
        <div className="bg-muted flex flex-col gap-1 rounded-r-lg p-3">
          {quickOptionsToUse.map((option) => (
            <Button
              className="justify-start text-left"
              key={option.name}
              onClick={() => {
                setLastPressedButton(option.name);
                const dateRangeValue = option.getValue();
                setDateRange(dateRangeValue);
                onValueChange(dateRangeValue);
              }}
              type="button"
              variant={
                lastPressedButton === option.name ? "secondary" : "ghost"
              }
            >
              {option.name}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const DateRangePicker = ({
  allowPastDates = true,
}: {
  allowPastDates?: boolean;
}) => {
  const [dateRange, setDateRange] = useSearchParamsDateRange();
  return (
    <DateRangeInput
      allowPastDates={allowPastDates}
      onValueChange={(v) => v.from && v.to && setDateRange(v.from, v.to)}
      placeholder={
        allowPastDates ? "Select a date range" : "Select projection dates"
      }
      value={dateRange}
    />
  );
};
